import { useRef, useState } from 'react'
import { Box, ButtonBase, Popover, Tooltip, Typography } from '@mui/material'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { KpiCascade } from './KpiCascade'
import { cascadeControlCount } from './cascadeControlCount'
import { CONTROLS } from '~/components/Charts/chrome/chartChrome'
import type { KpiCascadeState } from '~/state/useKpiCascadeState'

/** Wave-6 §4: the closed-state summary — display labels of the CURRENT selection,
 *  ' · '-joined; null driver/vt fall back to the first option (the effective seed the
 *  selects themselves display). Exported for tests. */
export function triggerLabel(state: KpiCascadeState): string {
  const parts: string[] = []
  parts.push(state.rootOptions.find((o) => o.value === state.root)?.label ?? state.root)
  if (state.axisCount >= 2 && state.driverOptions.length >= 1) {
    const d = state.driverOptions.find((o) => o.value === state.driver) ?? state.driverOptions[0]!
    parts.push(d.label)
  }
  if (state.axisCount === 3 && state.valueTypeOptions.length >= 1) {
    const v =
      state.valueTypeOptions.find((o) => o.value === state.valueType) ?? state.valueTypeOptions[0]!
    parts.push(v.label)
  }
  return parts.join(' · ')
}

/** Wave-6 §4 (D5, Option B): a ~190px select-look shell for multi-control cascades —
 *  the closed state SHOWS the full selection (state stays visible, the wave-4 rule);
 *  the popover houses the UNTOUCHED KpiCascade, whose selects apply immediately (the
 *  popover is a housing, not a form — no Apply button). 190px is a hard budget: at
 *  200px Dynamics' one-line row 1 no longer fits (spec §4 width note). The tooltip
 *  always carries the full label — a superset of the "when truncated" spec line,
 *  chosen over brittle overflow measurement. Single-control cascades render the bare
 *  KpiCascade instead (no extra click for no width win) — see the gate below. */
export function KpiCascadeTrigger({ state }: { state: KpiCascadeState }) {
  const [open, setOpen] = useState(false)
  const anchor = useRef<HTMLButtonElement>(null)
  const label = triggerLabel(state)
  // W6 gate-fix round 2: the bare/trigger gate lives HERE, not in the scaffold, and is
  // suspended while the popover is open. A root change INSIDE the popover can drop the
  // control count to 1 (bare root picked on a mixed page); an outside gate would unmount
  // the trigger mid-interaction — popover vanishes, keyboard focus falls to <body>
  // (reproduced live at the W6 gate). Keeping the mount while open lets the housed
  // cascade collapse gracefully to a lone root select; the gate re-evaluates on close.
  // Also absorbs the cold-load transient (empty options ⇒ bare select, then trigger).
  if (cascadeControlCount(state) < 2 && !open) return <KpiCascade state={state} />
  return (
    <>
      <Tooltip title={label}>
        <ButtonBase
          ref={anchor}
          data-testid="kpi-cascade-trigger"
          onClick={() => setOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={open}
          sx={{
            width: 190,
            justifyContent: 'space-between',
            border: '1px solid',
            borderColor: 'rgba(0, 0, 0, 0.23)',
            borderRadius: 1,
            px: 1.25,
            py: 0.5,
            textAlign: 'left',
            bgcolor: 'background.paper',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 10, color: 'text.secondary', lineHeight: 1.2 }}>
              {CONTROLS.kpi}
            </Typography>
            <Typography noWrap sx={{ fontSize: 13, lineHeight: 1.3 }}>
              {label}
            </Typography>
          </Box>
          <ArrowDropDownIcon fontSize="small" sx={{ color: 'text.secondary', flexShrink: 0 }} />
        </ButtonBase>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchor.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        // aria-haspopup="dialog" on the trigger promises a dialog; make the paper one.
        PaperProps={{ role: 'dialog', 'aria-label': CONTROLS.kpi }}
      >
        <Box sx={{ p: 2, minWidth: 220 }}>
          <KpiCascade state={state} vertical />
        </Box>
      </Popover>
    </>
  )
}
